import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  Alert,
  Button,
  Drawer,
  Icon,
  Input,
  Modal,
  PageHeading,
  Pills,
  Search,
  Section,
  Select,
  Stepper,
  Table,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition, SortDirection, SortState } from 'barte-design-system';
import {
  UploadSimple,
  FileCsv,
  X,
  CheckCircleIcon,
  ClockCounterClockwise,
  Trash,
  Storefront,
} from '@phosphor-icons/react';
import { MccTagInput } from './MccTagInput';
import './CadastroPlanoTaxas.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PlanoTaxa {
  id: number;
  nome: string;
  tipoAntecipado: boolean;
  tipoFluxoTempo: boolean;
  qtdSellers: number;
  dataCriacao: string; // ISO yyyy-mm-dd
  isDefaultAntecipado?: boolean;
  isDefaultFluxoTempo?: boolean;
}

type WizardStep = 'upload' | 'config' | 'review' | 'processing' | 'success';
type TipoPlano = 'antecipado' | 'fluxo-tempo';
type Modalidade = 'online' | 'pos' | 'ambos';

// ─── Mock company context ─────────────────────────────────────────────────────

const COMPANY_MOCK = {
  nome: 'BARTE BRASIL LTDA',
  id: '117',
  cnpj: '60.571.477/0001-59',
};

// ─── Mock plans (mirrors the current production screenshot) ───────────────────

const INITIAL_PLANOS: PlanoTaxa[] = [
  { id: 4, nome: 'Plano inicial', tipoAntecipado: false, tipoFluxoTempo: false, qtdSellers: 52, dataCriacao: '2024-08-14' },
  { id: 120, nome: 'Plano Antecipado', tipoAntecipado: false, tipoFluxoTempo: false, qtdSellers: 1, dataCriacao: '2025-02-03' },
  { id: 121, nome: 'Plano Fluxo do Tempo', tipoAntecipado: false, tipoFluxoTempo: false, qtdSellers: 1, dataCriacao: '2025-02-03' },
  { id: 187, nome: 'Nautt_Salabim - Calculadora de Taxas v02 [Original - Crie uma Cópia] - Antecipado (2).csv', tipoAntecipado: false, tipoFluxoTempo: false, qtdSellers: 1, dataCriacao: '2025-09-22' },
  { id: 202, nome: 'Ajuste de Taxas', tipoAntecipado: false, tipoFluxoTempo: false, qtdSellers: 1, dataCriacao: '2026-01-10' },
  { id: 203, nome: 'Ajuste de Taxas - Plano Antecipado', tipoAntecipado: true, tipoFluxoTempo: false, qtdSellers: 1, isDefaultAntecipado: true, dataCriacao: '2026-03-18' },
  { id: 204, nome: 'Ajuste de Taxas - Fluxo do Tempo', tipoAntecipado: false, tipoFluxoTempo: true, qtdSellers: 1, isDefaultFluxoTempo: true, dataCriacao: '2026-04-25' },
];

function formatBR(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function todayISO(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ─── Mock sellers (60 nomes realistas) ────────────────────────────────────────

const ALL_SELLERS: { id: string; nome: string; cnpj: string }[] = [
  { id: 'SLR-00001', nome: 'Mercado Bom Preço LTDA', cnpj: '12.345.678/0001-91' },
  { id: 'SLR-00002', nome: 'Padaria Pão Quente ME', cnpj: '23.456.789/0001-02' },
  { id: 'SLR-00003', nome: 'Loja Central de Departamentos', cnpj: '34.567.890/0001-13' },
  { id: 'SLR-00004', nome: 'Tech Store Eletrônicos S.A.', cnpj: '45.678.901/0001-24' },
  { id: 'SLR-00005', nome: 'FitLife Academia Esportes', cnpj: '56.789.012/0001-35' },
  { id: 'SLR-00006', nome: 'Ótica Visão Clara LTDA', cnpj: '67.890.123/0001-46' },
  { id: 'SLR-00007', nome: 'Pet Shop Animal Love', cnpj: '78.901.234/0001-57' },
  { id: 'SLR-00008', nome: 'Barbearia Corte Certo', cnpj: '89.012.345/0001-68' },
  { id: 'SLR-00009', nome: 'Lavanderia Express Sul', cnpj: '90.123.456/0001-79' },
  { id: 'SLR-00010', nome: 'Farmácia Saúde & Vida', cnpj: '11.234.567/0001-80' },
  { id: 'SLR-00011', nome: 'Livraria Cultura Brasília', cnpj: '22.345.678/0001-91' },
  { id: 'SLR-00012', nome: 'Restaurante Sabor Bom', cnpj: '33.456.789/0001-02' },
  { id: 'SLR-00013', nome: 'Supermercado Família Real', cnpj: '44.567.890/0001-13' },
  { id: 'SLR-00014', nome: 'Auto Peças Brasil LTDA', cnpj: '55.678.901/0001-24' },
  { id: 'SLR-00015', nome: 'Consultório Dr. Lima', cnpj: '66.789.012/0001-35' },
  { id: 'SLR-00016', nome: 'Norte Distribuidora S.A.', cnpj: '77.890.123/0001-46' },
  { id: 'SLR-00017', nome: 'Posto Combustível Star', cnpj: '88.901.234/0001-57' },
  { id: 'SLR-00018', nome: 'Varejo Express Comércio', cnpj: '99.012.345/0001-68' },
  { id: 'SLR-00019', nome: 'Grupo Alfa Soluções', cnpj: '10.123.456/0001-79' },
  { id: 'SLR-00020', nome: 'Beta Comércio e Serviços', cnpj: '21.234.567/0001-80' },
  { id: 'SLR-00021', nome: 'Allugator Serviços Digitais', cnpj: '32.345.678/0001-91' },
  { id: 'SLR-00022', nome: 'Razor Technologies do Brasil', cnpj: '43.456.789/0001-02' },
  { id: 'SLR-00023', nome: 'Desincha Comércio Naturais', cnpj: '54.567.890/0001-13' },
  { id: 'SLR-00024', nome: 'YouVisa Tecnologia Viagens', cnpj: '65.678.901/0001-24' },
  { id: 'SLR-00025', nome: 'EXP Saúde Clínica Médica', cnpj: '76.789.012/0001-35' },
  { id: 'SLR-00026', nome: 'Tech Ventures Brasil', cnpj: '87.890.123/0001-46' },
  { id: 'SLR-00027', nome: 'Grupo Comercial Alpha', cnpj: '98.901.234/0001-57' },
  { id: 'SLR-00028', nome: 'Farma Saúde e Bem-Estar', cnpj: '19.012.345/0001-68' },
  { id: 'SLR-00029', nome: 'Construção Forte LTDA', cnpj: '20.123.456/0001-79' },
  { id: 'SLR-00030', nome: 'Agro Sul Exportações S.A.', cnpj: '31.234.567/0001-80' },
  { id: 'SLR-00031', nome: 'Digital Pay Solutions', cnpj: '42.345.678/0001-91' },
  { id: 'SLR-00032', nome: 'Fashion Outlet Brasil', cnpj: '53.456.789/0001-02' },
  { id: 'SLR-00033', nome: 'Boutique Estilo Moderno', cnpj: '64.567.890/0001-13' },
  { id: 'SLR-00034', nome: 'Joalheria Brilhante LTDA', cnpj: '75.678.901/0001-24' },
  { id: 'SLR-00035', nome: 'Casa de Carnes Premium', cnpj: '86.789.012/0001-35' },
  { id: 'SLR-00036', nome: 'Hortifruti Mercado Verde', cnpj: '97.890.123/0001-46' },
  { id: 'SLR-00037', nome: 'Padaria Trigo Dourado', cnpj: '18.901.234/0001-57' },
  { id: 'SLR-00038', nome: 'Sorveteria Geladinho', cnpj: '29.012.345/0001-68' },
  { id: 'SLR-00039', nome: 'Lanchonete Esquina Feliz', cnpj: '30.123.456/0001-79' },
  { id: 'SLR-00040', nome: 'Pizzaria Forno a Lenha', cnpj: '41.234.567/0001-80' },
  { id: 'SLR-00041', nome: 'Salão Beleza Total', cnpj: '52.345.678/0001-91' },
  { id: 'SLR-00042', nome: 'Spa Recanto Bem-Estar', cnpj: '63.456.789/0001-02' },
  { id: 'SLR-00043', nome: 'Clínica Estética Renova', cnpj: '74.567.890/0001-13' },
  { id: 'SLR-00044', nome: 'Dental Care Odontologia', cnpj: '85.678.901/0001-24' },
  { id: 'SLR-00045', nome: 'Hospital São Lucas', cnpj: '96.789.012/0001-35' },
  { id: 'SLR-00046', nome: 'Laboratório Análises Clínicas', cnpj: '17.890.123/0001-46' },
  { id: 'SLR-00047', nome: 'Escola Saber Mais', cnpj: '28.901.234/0001-57' },
  { id: 'SLR-00048', nome: 'Curso de Idiomas Global', cnpj: '39.012.345/0001-68' },
  { id: 'SLR-00049', nome: 'Faculdade Horizonte', cnpj: '40.123.456/0001-79' },
  { id: 'SLR-00050', nome: 'Editora Caminhos do Saber', cnpj: '51.234.567/0001-80' },
  { id: 'SLR-00051', nome: 'Transportadora Rota Certa', cnpj: '62.345.678/0001-91' },
  { id: 'SLR-00052', nome: 'Logística Express Brasil', cnpj: '73.456.789/0001-02' },
  { id: 'SLR-00053', nome: 'Hotel Mar Azul Pousada', cnpj: '84.567.890/0001-13' },
  { id: 'SLR-00054', nome: 'Agência Turismo Aventura', cnpj: '95.678.901/0001-24' },
  { id: 'SLR-00055', nome: 'Casa de Festas Memórias', cnpj: '16.789.012/0001-35' },
  { id: 'SLR-00056', nome: 'Buffet Sabor & Arte', cnpj: '27.890.123/0001-46' },
  { id: 'SLR-00057', nome: 'Floricultura Jardim Belo', cnpj: '38.901.234/0001-57' },
  { id: 'SLR-00058', nome: 'Marcenaria Tradição', cnpj: '49.012.345/0001-68' },
  { id: 'SLR-00059', nome: 'Móveis Planejados Casa Nova', cnpj: '50.123.456/0001-79' },
  { id: 'SLR-00060', nome: 'Vidraçaria Cristal Puro', cnpj: '61.234.567/0001-80' },
];

// Distribuição inicial: plano inicial (id 4) com 52 sellers, demais com 1.
const INITIAL_SELLERS_BY_PLANO: Record<number, string[]> = {
  4: ALL_SELLERS.slice(0, 52).map((s) => s.id),
  120: ['SLR-00053'],
  121: ['SLR-00054'],
  187: ['SLR-00055'],
  202: ['SLR-00056'],
  203: ['SLR-00057'],
  204: ['SLR-00058'],
};

const STEPS = [
  { label: 'Upload' },
  { label: 'Configuração' },
  { label: 'Revisão' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function CadastroPlanoTaxas() {
  const navigate = useNavigate();
  const [planos, setPlanos] = useState<PlanoTaxa[]>(INITIAL_PLANOS);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [sortState, setSortState] = useState<SortState>({ key: 'dataCriacao', direction: 'desc' });

  // ── Sellers por plano + modal de gerenciamento ────────────────────────────
  const [sellersByPlano, setSellersByPlano] = useState<Record<number, string[]>>(INITIAL_SELLERS_BY_PLANO);
  const [sellersModalPlano, setSellersModalPlano] = useState<PlanoTaxa | null>(null);
  const [sellerQuery, setSellerQuery] = useState('');
  const [sellerSearchExpanded, setSellerSearchExpanded] = useState(false);
  const [sellerToAdd, setSellerToAdd] = useState<string>('');

  // Wizard state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [step, setStep] = useState<WizardStep>('upload');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [nomePlano, setNomePlano] = useState('');
  const [mccs, setMccs] = useState<string[]>([]);
  const [tipo, setTipo] = useState<TipoPlano | ''>('');
  const [modalidade, setModalidade] = useState<Modalidade | ''>('');
  const [showStepErrors, setShowStepErrors] = useState(false);

  const [processingPhase, setProcessingPhase] = useState<0 | 1 | 2>(0);
  const [novoPlanoCriado, setNovoPlanoCriado] = useState<PlanoTaxa | null>(null);

  // Cancel confirmation modal (overlay on top of Drawer)
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Detail drawer placeholder (out-of-scope content)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPlano] = useState<PlanoTaxa | null>(null);

  // ─── Wizard navigation ──────────────────────────────────────────────────────

  function resetWizard() {
    setStep('upload');
    setCsvFile(null);
    setUploadError(null);
    setNomePlano('');
    setMccs([]);
    setTipo('');
    setModalidade('');
    setShowStepErrors(false);
    setProcessingPhase(0);
    setNovoPlanoCriado(null);
  }

  function openWizard() {
    resetWizard();
    setDrawerOpen(true);
  }

  function closeWizard() {
    setDrawerOpen(false);
    setCancelModalOpen(false);
    setTimeout(resetWizard, 300);
  }

  function requestCancel() {
    // Se está em processing/success, não há confirmação: só fecha.
    if (step === 'processing') return; // não permite cancelar processamento
    if (step === 'success') { closeWizard(); return; }

    // Se está em input, pergunta se há progresso.
    const hasProgress = csvFile !== null || nomePlano !== '' || mccs.length > 0 || tipo !== '' || modalidade !== '';
    if (hasProgress) {
      setCancelModalOpen(true);
    } else {
      closeWizard();
    }
  }

  // ─── File upload (Step 1) ───────────────────────────────────────────────────

  function validateAndSetFile(file: File | null) {
    setUploadError(null);
    if (!file) { setCsvFile(null); return; }
    const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
    if (!isCsv) {
      setUploadError('Formato inválido. Aceitamos apenas CSV.');
      setCsvFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('O arquivo excede o limite de 5 MB.');
      setCsvFile(null);
      return;
    }
    setCsvFile(file);
  }

  function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    validateAndSetFile(file);
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    validateAndSetFile(file);
  }

  // ─── Step 2 validation ──────────────────────────────────────────────────────

  const step2Errors = useMemo(() => {
    const errs: { nome?: string; mccs?: string; tipo?: string; modalidade?: string } = {};
    if (!nomePlano.trim()) errs.nome = 'Nome é obrigatório.';
    if (mccs.length === 0) errs.mccs = 'Adicione pelo menos um MCC.';
    if (!tipo) errs.tipo = 'Selecione um tipo.';
    if (!modalidade) errs.modalidade = 'Selecione uma modalidade.';
    return errs;
  }, [nomePlano, mccs, tipo, modalidade]);

  const step2Valid = Object.keys(step2Errors).length === 0;

  // ─── Step 3 — derived numbers ───────────────────────────────────────────────

  // O CSV de "base de cálculo" tem ~25 cells. O sistema expande:
  //   24 parcelas (a partir das 4 faixas) × 5 bandeiras × 6 dias de repasse = 720 (crédito)
  //   + 3 métodos (PIX, Boleto, Débito) × 6 dias = 18
  // Quando "Ambos" (Online + POS), multiplica por 2.
  // E cada combinação se aplica a cada MCC selecionado.
  const totalTaxas = useMemo(() => {
    const base = 24 * 5 * 6 + 3 * 6; // 738 por canal
    const canais = modalidade === 'ambos' ? 2 : 1;
    return base * canais * Math.max(mccs.length, 1);
  }, [mccs, modalidade]);

  // ─── Avançar wizard ─────────────────────────────────────────────────────────

  function handleAdvance() {
    if (step === 'upload') {
      if (!csvFile) { setUploadError('Selecione um arquivo CSV.'); return; }
      setStep('config');
    } else if (step === 'config') {
      if (!step2Valid) { setShowStepErrors(true); return; }
      setShowStepErrors(false);
      setStep('review');
    } else if (step === 'review') {
      setStep('processing');
    }
  }

  function handleBack() {
    if (step === 'config') setStep('upload');
    else if (step === 'review') setStep('config');
  }

  // ─── Processing simulation ──────────────────────────────────────────────────

  useEffect(() => {
    if (step !== 'processing') return;
    setProcessingPhase(0);
    // Calculando… (3s) → Processando… (3s) → Sucesso! (1.5s) → tela de sucesso
    const t1 = setTimeout(() => setProcessingPhase(1), 3000);
    const t2 = setTimeout(() => {
      setProcessingPhase(2);
      // Cria o plano ao chegar em "Sucesso!"
      const novoId = Math.max(...planos.map((p) => p.id)) + 1;
      const novo: PlanoTaxa = {
        id: novoId,
        nome: nomePlano.trim(),
        tipoAntecipado: tipo === 'antecipado',
        tipoFluxoTempo: tipo === 'fluxo-tempo',
        qtdSellers: 0,
        dataCriacao: todayISO(),
      };
      setPlanos((prev) => [...prev, novo]);
      setNovoPlanoCriado(novo);
    }, 6000);
    const t3 = setTimeout(() => setStep('success'), 7500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Table ──────────────────────────────────────────────────────────────────

  const columns: ColumnDefinition<PlanoTaxa>[] = [
    { key: 'id', title: 'ID', dataIndex: 'id', width: 70, render: (v) => <Text variant="body-sm">{v as number}</Text> },
    {
      key: 'nome',
      title: 'Nome',
      dataIndex: 'nome',
      sortable: true,
      render: (v) => <Text variant="body-sm">{v as string}</Text>,
    },
    {
      key: 'dataCriacao',
      title: 'Data de criação',
      dataIndex: 'dataCriacao',
      width: 140,
      sortable: true,
      render: (v) => <Text variant="body-sm">{formatBR(v as string)}</Text>,
    },
    {
      key: 'planoAntecipado',
      title: 'Plano Antecipado',
      dataIndex: 'tipoAntecipado',
      width: 170,
      render: (_v, r) => r.isDefaultAntecipado
        ? <Pills label="Plano default" state="success" variant="fill" size="sm" />
        : <Text variant="body-sm">—</Text>,
    },
    {
      key: 'planoFluxoTempo',
      title: 'Plano Fluxo do tempo',
      dataIndex: 'tipoFluxoTempo',
      width: 180,
      render: (_v, r) => r.isDefaultFluxoTempo
        ? <Pills label="Plano default" state="success" variant="fill" size="sm" />
        : <Text variant="body-sm">—</Text>,
    },
    {
      key: 'qtdSellers',
      title: 'Qtd. Sellers',
      dataIndex: 'qtdSellers',
      width: 120,
      render: (_v, r) => {
        const count = sellersByPlano[r.id]?.length ?? 0;
        return (
          <button
            type="button"
            className="cpt-sellers-pill-btn"
            onClick={(e) => { e.stopPropagation(); openSellersModal(r); }}
            aria-label={`Ver e gerenciar ${count} seller${count !== 1 ? 's' : ''} do plano ${r.nome}`}
          >
            <Pills
              label={`${count} seller${count !== 1 ? 's' : ''}`}
              state={count > 0 ? 'info' : 'default'}
              variant="light"
              size="sm"
              icon={Storefront}
            />
          </button>
        );
      },
    },
    {
      key: 'actions',
      title: '',
      dataIndex: 'id',
      width: 180,
      render: (_v, r) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => { e.stopPropagation(); /* placeholder */ }}
        >
          Definir como Padrão
        </Button>
      ),
    },
  ];

  // Ordenação: aplica sortState antes da paginação
  const sortedPlanos = useMemo(() => {
    if (!sortState.direction) return planos;
    const arr = [...planos];
    arr.sort((a, b) => {
      let av: string | number = '';
      let bv: string | number = '';
      if (sortState.key === 'nome') {
        av = a.nome.toLowerCase();
        bv = b.nome.toLowerCase();
      } else if (sortState.key === 'dataCriacao') {
        av = a.dataCriacao;
        bv = b.dataCriacao;
      }
      if (av < bv) return sortState.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortState.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [planos, sortState]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedPlanos.slice(start, start + pageSize);
  }, [page, sortedPlanos]);

  const totalPages = Math.ceil(sortedPlanos.length / pageSize);

  function handleSort(key: string, direction: SortDirection) {
    setSortState({ key, direction });
    setPage(1);
  }

  // ── Sellers modal handlers ──────────────────────────────────────────────────

  function openSellersModal(plano: PlanoTaxa) {
    setSellersModalPlano(plano);
    setSellerQuery('');
    setSellerToAdd('');
    setSellerSearchExpanded(false);
  }

  function closeSellersModal() {
    setSellersModalPlano(null);
    setSellerQuery('');
    setSellerToAdd('');
    setSellerSearchExpanded(false);
  }

  function removeSellerFromPlano(sellerId: string) {
    if (!sellersModalPlano) return;
    setSellersByPlano((prev) => ({
      ...prev,
      [sellersModalPlano.id]: (prev[sellersModalPlano.id] ?? []).filter((s) => s !== sellerId),
    }));
  }

  function addSellerToPlano(sellerId: string) {
    if (!sellersModalPlano || !sellerId) return;
    setSellersByPlano((prev) => {
      const current = prev[sellersModalPlano.id] ?? [];
      if (current.includes(sellerId)) return prev;
      return { ...prev, [sellersModalPlano.id]: [...current, sellerId] };
    });
    setSellerToAdd('');
  }

  const modalSellersInPlano = useMemo(
    () => (sellersModalPlano ? (sellersByPlano[sellersModalPlano.id] ?? []) : []),
    [sellersModalPlano, sellersByPlano],
  );

  const modalAvailableSellers = ALL_SELLERS.filter((s) => !modalSellersInPlano.includes(s.id));

  const modalFilteredSellers = useMemo(() => {
    const q = sellerQuery.trim().toLowerCase();
    if (!q) return modalSellersInPlano;
    return modalSellersInPlano.filter((id) => {
      const s = ALL_SELLERS.find((x) => x.id === id);
      if (!s) return false;
      return (
        s.id.toLowerCase().includes(q) ||
        s.nome.toLowerCase().includes(q) ||
        s.cnpj.includes(q)
      );
    });
  }, [modalSellersInPlano, sellerQuery]);

  // ─── Drawer content per step ────────────────────────────────────────────────

  const currentStepIndex = step === 'upload' ? 1 : step === 'config' ? 2 : 3;

  function renderUploadStep() {
    return (
      <div className="cpt-drawer-body">
        <Text variant="body-base">
          Faça upload do CSV com a base de cálculo do plano.
        </Text>

        {!csvFile && (
          <label
            htmlFor="cpt-file-input"
            className={`cpt-dropzone${isDragging ? ' is-dragging' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
          >
            <div className="cpt-dropzone-icon">
              <Icon icon={UploadSimple} size={32} weight="regular" />
            </div>
            <Text variant="body-base">
              <span className="cpt-dropzone-link">Clique para fazer upload</span> ou arraste e solte
            </Text>
            <Text variant="caption-md">Arquivo suportado: CSV (até 5 MB)</Text>
            <input
              id="cpt-file-input"
              type="file"
              accept=".csv"
              className="cpt-dropzone-input"
              onChange={onFilePick}
            />
          </label>
        )}

        {csvFile && (
          <div className="cpt-file-card">
            <Icon icon={FileCsv} size={28} weight="regular" />
            <div className="cpt-file-card-meta">
              <Text variant="label-base" className="cpt-file-card-name">{csvFile.name}</Text>
              <Text variant="caption-md" className="cpt-file-card-size">
                {(csvFile.size / 1024).toFixed(1)} KB
              </Text>
            </div>
            <button
              className="cpt-file-card-remove"
              onClick={() => setCsvFile(null)}
              aria-label="Remover arquivo"
              type="button"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        )}

        {uploadError && (
          <Text variant="body-sm" style={{ color: 'var(--accent-red, #D5292A)' }}>
            {uploadError}
          </Text>
        )}
      </div>
    );
  }

  function renderConfigStep() {
    return (
      <div className="cpt-drawer-body">
        <div className="cpt-form-stack">
          <Input
            label="Nome do plano"
            placeholder="Ex.: Plano antecipado Go Pagos"
            value={nomePlano}
            onChange={(e) => setNomePlano((e.target as HTMLInputElement).value)}
            error={showStepErrors && step2Errors.nome ? step2Errors.nome : false}
          />

          <MccTagInput
            value={mccs}
            onChange={setMccs}
            error={showStepErrors && step2Errors.mccs ? step2Errors.mccs : undefined}
          />

          <Select
            label="Tipo do plano"
            placeholder="Selecione o tipo"
            value={tipo}
            onChange={(v) => setTipo(v as TipoPlano)}
            options={[
              { value: 'antecipado', label: 'Antecipado' },
              { value: 'fluxo-tempo', label: 'Fluxo do tempo' },
            ]}
            helpText={showStepErrors && step2Errors.tipo ? step2Errors.tipo : undefined}
          />

          <Select
            label="Modalidade"
            placeholder="Selecione a modalidade"
            value={modalidade}
            onChange={(v) => setModalidade(v as Modalidade)}
            options={[
              { value: 'online', label: 'Online' },
              { value: 'pos', label: 'POS' },
              { value: 'ambos', label: 'Online + POS' },
            ]}
            helpText={showStepErrors && step2Errors.modalidade ? step2Errors.modalidade : undefined}
          />
        </div>
      </div>
    );
  }

  function renderReviewStep() {
    const tipoLabel = tipo === 'antecipado' ? 'Antecipado' : 'Fluxo do tempo';
    const modalidadeLabel = modalidade === 'online' ? 'Online' : modalidade === 'pos' ? 'POS' : 'Online + POS';

    return (
      <div className="cpt-drawer-body">
        <Text variant="body-base">
          Confirme as informações antes de processar.
        </Text>

        <Section title="" className="cpt-section-untitled">
          <Section.Row label="Nome do plano" content={<Text variant="label-sm">{nomePlano}</Text>} />
          <Section.Row label="Tipo" content={<Text variant="label-sm">{tipoLabel}</Text>} />
          <Section.Row label="Modalidade" content={<Text variant="label-sm">{modalidadeLabel}</Text>} />
          <Section.Row
            label="MCCs"
            content={
              <div className="cpt-review-mccs">
                {mccs.map((m) => (
                  <Pills key={m} label={m} variant="light" state="default" size="sm" />
                ))}
              </div>
            }
          />
          <Section.Row
            label="Total de taxas a criar"
            content={<Text variant="label-sm">{totalTaxas.toLocaleString('pt-BR')} taxas</Text>}
          />
        </Section>

        <Alert
          variant="info"
          title="O sistema vai gerar automaticamente as variações"
          description="Parcela (1 a 24), bandeira (Mastercard, Visa, Elo, Amex, Hiper), dias de repasse (D+1, D+2, D+3, D+10, D+14, D+30) e canal a partir da base de cálculo enviada."
          showCloseButton={false}
        />
      </div>
    );
  }

  function renderProcessingStep() {
    const phases = [
      'Calculando variações de taxa…',
      'Processando cadastro…',
      'Sucesso!',
    ];
    return (
      <div className="cpt-state-screen">
        {processingPhase < 2 ? (
          <div className="cpt-spinner" aria-label="Processando" />
        ) : (
          <Icon icon={CheckCircleIcon} size={56} weight="fill" className="cpt-success-icon" />
        )}
        <Text variant="body-xl" className="cpt-state-screen-title">
          {phases[processingPhase]}
        </Text>

        <div className="cpt-phase-list" role="status" aria-live="polite">
          {phases.map((label, idx) => (
            <div
              key={idx}
              className={`cpt-phase-item${idx === processingPhase ? ' is-active' : ''}${idx < processingPhase ? ' is-done' : ''}`}
            >
              <span className="cpt-phase-bullet" />
              <Text variant="body-sm">{label}</Text>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderSuccessStep() {
    // Confete leve — 14 partículas em ângulos variados saindo do check.
    const CONFETTI_COLORS = ['#EF3B7D', '#21A23E', '#1D70B8', '#F5A623', '#7B61FF'];
    const confettiCount = 14;
    return (
      <div className="cpt-state-screen">
        <div className="cpt-success-wrap">
          <Icon icon={CheckCircleIcon} size={56} weight="fill" className="cpt-success-icon" />
          <div className="cpt-confetti" aria-hidden>
            {Array.from({ length: confettiCount }).map((_, i) => {
              const angle = (360 / confettiCount) * i;
              const distance = 56 + (i % 3) * 12;
              const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
              const delay = (i % 5) * 40;
              return (
                <span
                  key={i}
                  className="cpt-confetti-piece"
                  style={{
                    ['--angle' as any]: `${angle}deg`,
                    ['--distance' as any]: `${distance}px`,
                    ['--delay' as any]: `${delay}ms`,
                    background: color,
                  }}
                />
              );
            })}
          </div>
        </div>
        <Text variant="body-xl" className="cpt-state-screen-title">
          Plano "{nomePlano}" criado com sucesso
        </Text>
        <Pills
          label={`${totalTaxas.toLocaleString('pt-BR')} taxas cadastradas em ${mccs.length} MCC${mccs.length !== 1 ? 's' : ''}`}
          state="success"
          variant="light"
          size="base"
        />
      </div>
    );
  }

  // ─── Drawer footer per step ─────────────────────────────────────────────────

  const drawerFooter = useMemo(() => {
    if (step === 'processing') return undefined; // sem ações enquanto processa
    if (step === 'success') {
      return {
        primaryButton: {
          children: 'Ver plano',
          onClick: () => {
            const id = novoPlanoCriado?.id;
            closeWizard();
            if (id) navigate(`/backoffice/taxas-company/plano/${id}`);
          },
        },
        secondaryButton: {
          children: 'Fechar',
          variant: 'outline' as const,
          onClick: closeWizard,
        },
      };
    }
    if (step === 'upload') {
      return {
        primaryButton: {
          children: 'Avançar',
          onClick: handleAdvance,
          disabled: !csvFile,
        },
        secondaryButton: {
          children: 'Cancelar',
          variant: 'ghost' as const,
          onClick: requestCancel,
        },
      };
    }
    if (step === 'config') {
      return {
        primaryButton: {
          children: 'Avançar',
          onClick: handleAdvance,
        },
        secondaryButton: {
          children: 'Voltar',
          variant: 'outline' as const,
          onClick: handleBack,
        },
      };
    }
    // review
    return {
      primaryButton: {
        children: 'Confirmar e processar',
        onClick: handleAdvance,
      },
      secondaryButton: {
        children: 'Voltar',
        variant: 'outline' as const,
        onClick: handleBack,
      },
    };
  }, [step, csvFile, novoPlanoCriado]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render ────────────────────────────────────────────────────────────────

  const drawerTitle =
    step === 'processing' ? 'Processando…' :
    step === 'success' ? 'Plano criado' :
    'Criar plano de taxas';

  const showStepper = step === 'upload' || step === 'config' || step === 'review';

  return (
    <>
      <BackofficeLayout activeItem="taxas-company-upload-csv">
        <main className="content">
          <div className="content-inner">
            <PageHeading title={COMPANY_MOCK.nome} />

            <div className="cpt-company-context">
              <Text variant="label-base" className="cpt-company-context-title">
                Detalhes principais
              </Text>
              <div className="cpt-company-context-grid">
                <div className="cpt-company-context-cell">
                  <Text variant="caption-md" className="cpt-company-context-cell-label">ID da company</Text>
                  <Text variant="label-base" className="cpt-company-context-cell-value">{COMPANY_MOCK.id}</Text>
                </div>
                <div className="cpt-company-context-cell">
                  <Text variant="caption-md" className="cpt-company-context-cell-label">CNPJ</Text>
                  <Text variant="label-base" className="cpt-company-context-cell-value">{COMPANY_MOCK.cnpj}</Text>
                </div>
                <div className="cpt-company-context-cell">
                  <Text variant="caption-md" className="cpt-company-context-cell-label">Razão social</Text>
                  <Text variant="label-base" className="cpt-company-context-cell-value">{COMPANY_MOCK.nome}</Text>
                </div>
              </div>
            </div>

            <div className="cpt-section-heading">
              <Text variant="body-lg">Planos da empresa</Text>
              <div className="cpt-section-heading-actions">
                <Button
                  variant="ghost"
                  size="base"
                  iconLeft={<Icon icon={ClockCounterClockwise} size={16} weight="regular" />}
                  onClick={() => {}}
                >
                  Ver histórico
                </Button>
                <Button variant="outline" size="base" onClick={() => {}}>
                  Criar manualmente
                </Button>
                <Button variant="fill" size="base" onClick={openWizard}>
                  Criar com wizard
                </Button>
              </div>
            </div>

            <div className="content-table cpt-content-table">
              <Table<PlanoTaxa>
                data={pagedData}
                columns={columns}
                rowKey={(r) => r.id}
                emptyText="Nenhum plano cadastrado."
                sortState={sortState}
                onSort={handleSort}
                pagination={{
                  currentPage: page,
                  totalPages,
                  totalItems: sortedPlanos.length,
                  onPageChange: setPage,
                }}
              />
            </div>
          </div>
        </main>
      </BackofficeLayout>

      {/* ── Drawer: Criar plano (fluxo de 3 steps + processing + success) ── */}
      <Drawer
        isOpen={drawerOpen}
        title={drawerTitle}
        onClose={requestCancel}
        footer={drawerFooter}
      >
        {showStepper && (
          <div className="cpt-stepper-wrapper">
            <Stepper steps={STEPS} currentStep={currentStepIndex} />
          </div>
        )}

        {step === 'upload' && renderUploadStep()}
        {step === 'config' && renderConfigStep()}
        {step === 'review' && renderReviewStep()}
        {step === 'processing' && renderProcessingStep()}
        {step === 'success' && renderSuccessStep()}
      </Drawer>

      {/* ── Modal de confirmação de cancelamento (sobre o Drawer) ─────────── */}
      <Modal
        isOpen={cancelModalOpen}
        title="Cancelar criação do plano?"
        onClose={() => setCancelModalOpen(false)}
        size="sm"
        className="cpt-cancel-modal"
        footer={{
          primaryButton: {
            children: 'Sim, cancelar',
            onClick: closeWizard,
          },
          secondaryButton: {
            children: 'Continuar editando',
            variant: 'outline',
            onClick: () => setCancelModalOpen(false),
          },
        }}
      >
        <Text variant="body-sm">
          Os dados preenchidos serão perdidos. Tem certeza que deseja cancelar?
        </Text>
      </Modal>

      {/* ── Drawer de detalhe do plano (placeholder — fora do escopo) ─────── */}
      <Drawer
        isOpen={detailOpen}
        title={detailPlano?.nome ?? 'Detalhe do plano'}
        onClose={() => setDetailOpen(false)}
      >
        <div className="cpt-drawer-body">
          <Section title="Plano">
            <Section.Row label="ID" content={<Text variant="label-sm">{detailPlano?.id ?? '—'}</Text>} />
            <Section.Row label="Nome" content={<Text variant="label-sm">{detailPlano?.nome ?? '—'}</Text>} />
            <Section.Row
              label="Tipo"
              content={<Text variant="label-sm">{detailPlano?.tipoAntecipado ? 'Antecipado' : detailPlano?.tipoFluxoTempo ? 'Fluxo do tempo' : '—'}</Text>}
            />
          </Section>
          <div className="cpt-review-callout">
            <Text variant="caption-md">
              Conteúdo completo do detalhe do plano (lista de taxas, edição, histórico) será especificado em entrega futura — fora do escopo desta iteração.
            </Text>
          </div>
        </div>
      </Drawer>

      {/* ── Modal: gerenciar sellers do plano ─────────────────────────────── */}
      <Modal
        isOpen={sellersModalPlano !== null}
        title={`Sellers do plano "${sellersModalPlano?.nome ?? ''}"`}
        onClose={closeSellersModal}
        size="lg"
        footer={{
          primaryButton: {
            children: 'Fechar',
            onClick: closeSellersModal,
          },
        }}
      >
        <div className="cpt-sellers-modal">
          <div className="cpt-sellers-modal-add">
            <Select
              placeholder="Adicionar seller ao plano"
              value={sellerToAdd}
              onChange={(v) => setSellerToAdd(v)}
              searchable
              options={modalAvailableSellers.map((s) => ({
                value: s.id,
                label: `${s.id} — ${s.nome}`,
              }))}
            />
            <Button
              variant="fill"
              size="base"
              disabled={!sellerToAdd}
              onClick={() => addSellerToPlano(sellerToAdd)}
            >
              Adicionar
            </Button>
          </div>

          <div className="cpt-sellers-modal-summary">
            <Pills
              label={`${modalSellersInPlano.length} seller${modalSellersInPlano.length !== 1 ? 's' : ''} no plano`}
              state="info"
              variant="light"
              size="sm"
              icon={Storefront}
            />
            {sellerQuery && (
              <Text variant="caption-md" className="cpt-sellers-modal-filter">
                {modalFilteredSellers.length} resultado{modalFilteredSellers.length !== 1 ? 's' : ''} para "{sellerQuery}"
              </Text>
            )}
            <div className="cpt-sellers-modal-search">
              <Search
                expanded={sellerSearchExpanded}
                value={sellerQuery}
                onChange={setSellerQuery}
                onExpand={(expanded) => {
                  setSellerSearchExpanded(expanded);
                  if (!expanded) setSellerQuery('');
                }}
                placeholder="Buscar por ID, nome ou CNPJ"
              />
            </div>
          </div>

          <div className="cpt-sellers-modal-list">
            {modalFilteredSellers.length === 0 && (
              <div className="cpt-sellers-modal-empty">
                <Text variant="body-sm">
                  {sellerQuery
                    ? 'Nenhum seller encontrado com esse filtro.'
                    : 'Nenhum seller vinculado a este plano. Use o campo acima para adicionar.'}
                </Text>
              </div>
            )}
            {modalFilteredSellers.map((id) => {
              const s = ALL_SELLERS.find((x) => x.id === id);
              if (!s) return null;
              return (
                <div key={id} className="cpt-sellers-modal-row">
                  <div className="cpt-sellers-modal-row-info">
                    <Text variant="label-sm">{s.nome}</Text>
                    <Text variant="caption-md" className="cpt-sellers-modal-row-meta">
                      {s.id} · {s.cnpj}
                    </Text>
                  </div>
                  <button
                    type="button"
                    className="cpt-sellers-modal-row-remove"
                    onClick={() => removeSellerFromPlano(id)}
                    aria-label={`Remover ${s.nome}`}
                  >
                    <Icon icon={Trash} size={16} weight="regular" />
                    Remover
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    </>
  );
}
